import type { CustomField } from "@web-app/types";

import {
  SQL_COLUMNS,
  SQL_MEASURES,
  customFieldSlug,
} from "../components/SQLEditor";

const RESERVED_IDENTIFIERS = new Set([
  ...SQL_COLUMNS.map((c) => c.name),
  ...SQL_MEASURES.map((m) => m.name),
]);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Split a SQL string into segments, tagging which ones fall inside single-quoted
 * string literals, so identifier replacement can skip over literal text (e.g.
 * `WHERE name = 'jane'` shouldn't be rewritten just because a custom field slugs
 * to "jane"). Postgres escapes a literal quote as '' within a string literal.
 */
function splitOutsideStringLiterals(
  sql: string,
): { inString: boolean; text: string }[] {
  const segments: { inString: boolean; text: string }[] = [];
  const stringLiteral = /'(?:[^']|'')*'/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = stringLiteral.exec(sql)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        inString: false,
        text: sql.slice(lastIndex, match.index),
      });
    }
    segments.push({ inString: true, text: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < sql.length) {
    segments.push({ inString: false, text: sql.slice(lastIndex) });
  }

  return segments;
}

/**
 * Custom field slugs eligible for SQL rewriting. Any slug that collides with a
 * real schema column or measure name (e.g. a custom field named "Status") is
 * dropped so we never rewrite a reference to a real column into a custom-field
 * reference.
 */
export function getTransformableCustomFieldSlugs(
  customFields: Pick<CustomField, "name">[],
): string[] {
  const slugs = new Set<string>();
  for (const field of customFields) {
    const slug = customFieldSlug(field.name);
    if (slug && !RESERVED_IDENTIFIERS.has(slug)) {
      slugs.add(slug);
    }
  }
  return Array.from(slugs);
}

/**
 * Rewrite bare custom-field slugs in a user-authored SQL query into their fully
 * qualified `custom_fields_{slug}` column names. Text inside string literals is
 * left untouched so filter values aren't mistaken for column references.
 */
export function transformSQLQuery(
  sql: string,
  customFieldSlugs: string[],
): string {
  if (!customFieldSlugs.length) return sql;

  return splitOutsideStringLiterals(sql)
    .map(({ inString, text }) => {
      if (inString) return text;

      let transformed = text;
      customFieldSlugs.forEach((slug) => {
        // Match slug only if NOT already qualified as "custom_fields_{slug}"
        const regex = new RegExp(
          `(?<!custom_fields_)\\b${escapeRegExp(slug)}\\b`,
          "g",
        );
        transformed = transformed.replace(regex, `custom_fields_${slug}`);
      });
      return transformed;
    })
    .join("");
}
